import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, LogOut, Users, Clock, Receipt, Film, Clapperboard, ArrowRight, BookOpen } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, StatusChip } from "@/components/Common";
import { getTheme } from "@/lib/eventTheme";

function deadlineLabel(iso) {
  if (!iso) return "No deadline";
  const d = new Date(iso);
  const days = Math.ceil((d - new Date()) / 86400000);
  if (days < 0) return "Closed";
  if (days === 0) return "Closes today";
  return `${days} day${days === 1 ? "" : "s"} left`;
}

export function TopBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="border-b border-[hsl(var(--border))] bg-[hsl(var(--card))]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Logo />
        <div className="flex items-center gap-3">
          <span className="hidden sm:block text-sm text-[hsl(var(--muted-foreground))]">{user?.name || user?.email}</span>
          <button data-testid="billing-link" onClick={() => navigate("/billing")} title="Billing & plans" className="h-9 w-9 grid place-items-center rounded-xl hover:bg-[hsl(var(--muted))] transition-colors">
            <Receipt size={18} />
          </button>
          <button data-testid="settings-link" onClick={() => navigate("/settings")} title="Settings" className="h-9 w-9 grid place-items-center rounded-xl hover:bg-[hsl(var(--muted))] transition-colors">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
          </button>
          <div className="h-9 w-9 rounded-full grid place-items-center text-sm font-semibold text-white overflow-hidden" style={{ background: "hsl(35 38% 45%)" }}>
            {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name || user?.email || "?").charAt(0).toUpperCase()}
          </div>
          <button data-testid="logout-button" onClick={async () => { await logout(); navigate("/"); }} title="Sign out"
            className="h-9 w-9 grid place-items-center rounded-xl hover:bg-[hsl(var(--muted))] transition-colors"><LogOut size={18} /></button>
        </div>
      </div>
    </header>
  );
}

const MODE_LABEL = { living: "Living portrait", in_memory: "In memory" };
const TYPE_LABEL = { solo: "Solo", couple: "Couple", chorus: "Chorus" };

function StoryCard({ s, onOpen }) {
  const pct = s.coverage?.total ? Math.round((s.coverage.done / s.coverage.total) * 100) : 0;
  return (
    <button data-testid={`story-card-${s.story_id}`} onClick={onOpen}
      className="text-left rounded-2xl border border-[hsl(var(--film-border))] bg-[hsl(var(--card))] overflow-hidden hover:shadow-[var(--film-shadow)] transition-shadow duration-200">
      <div className="h-2 bg-[hsl(var(--letterbox))]" />
      <div className="p-5 sm:p-6">
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
          <Clapperboard size={13} /> {TYPE_LABEL[s.type] || "Story"} · {MODE_LABEL[s.mode] || s.mode}
        </div>
        <h3 className="mt-1.5 font-display text-xl font-semibold truncate">{s.title}</h3>
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-[hsl(var(--muted-foreground))]">
            <span>Chapters covered</span>
            <span className="font-mono tabular-nums">{s.coverage?.done || 0}/{s.coverage?.total || 9}</span>
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
            <div className="h-full rounded-full bg-[hsl(var(--accent))] transition-[width] duration-500" style={{ width: `${Math.max(3, pct)}%` }} />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3 text-xs text-[hsl(var(--muted-foreground))] font-mono">
          <span className="inline-flex items-center gap-1"><Users size={13} /> {s.coverage?.answered || 0} answers</span>
          <span className="inline-flex items-center gap-1"><BookOpen size={13} /> {(s.archive_counts?.photo || 0) + (s.archive_counts?.video || 0)} archive</span>
        </div>
      </div>
    </button>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [ev, st] = await Promise.all([
        api.get("/events"),
        api.get("/stories").catch(() => ({ data: { stories: [] } })),
      ]);
      setEvents(ev.data || []);
      setStories(st.data?.stories || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/login");
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">Welcome back</h1>
          <p className="mt-1 text-[hsl(var(--muted-foreground))]">Two ways to honor the people who matter — gather voices for a moment, or tell a whole life story.</p>
        </div>

        {/* Two create paths */}
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <button data-testid="dashboard-create-tribute-button" onClick={() => navigate("/create")}
            className="group text-left rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 sm:p-7 hover:shadow-[var(--film-shadow)] transition-shadow duration-200">
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 grid place-items-center rounded-xl bg-[hsl(var(--theme-accent-soft))] text-[hsl(var(--accent))]"><Film size={20} /></div>
              <ArrowRight size={18} className="text-[hsl(var(--muted-foreground))] group-hover:translate-x-0.5 transition-transform" />
            </div>
            <h2 className="mt-4 font-display text-xl font-semibold">Collect tributes for an event</h2>
            <p className="mt-1.5 text-sm text-[hsl(var(--muted-foreground))]">Invite friends and family to record short messages. We weave them into one tribute film.</p>
            <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[hsl(var(--accent))]"><Plus size={16} /> New tribute</span>
          </button>

          <button data-testid="dashboard-create-story-button" onClick={() => navigate("/stories/new")}
            className="group text-left rounded-2xl border border-[hsl(var(--film-border))] bg-[hsl(var(--card))] overflow-hidden hover:shadow-[var(--film-shadow)] transition-shadow duration-200">
            <div className="p-6 sm:p-7">
              <div className="flex items-start justify-between">
                <div className="h-11 w-11 grid place-items-center rounded-xl bg-[hsl(var(--letterbox))] text-[hsl(var(--letterbox-foreground))]"><Clapperboard size={20} /></div>
                <ArrowRight size={18} className="text-[hsl(var(--muted-foreground))] group-hover:translate-x-0.5 transition-transform" />
              </div>
              <div className="mt-4 flex items-center gap-2">
                <h2 className="font-display text-xl font-semibold">Create a Keepsake Story</h2>
                <span className="font-mono text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]">New</span>
              </div>
              <p className="mt-1.5 text-sm text-[hsl(var(--muted-foreground))]">An AI-guided documentary of a whole life. A gentle interviewer asks the right questions — you record from home.</p>
              <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[hsl(var(--accent))]"><Plus size={16} /> Begin a story</span>
            </div>
          </button>
        </div>

        {/* Stories */}
        {(loading || stories.length > 0) && (
          <section className="mt-12">
            <h2 className="font-display text-2xl font-semibold">Your stories</h2>
            {loading ? (
              <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[0,1].map(i => <div key={i} className="h-44 rounded-2xl bg-[hsl(var(--muted))] animate-pulse" />)}</div>
            ) : (
              <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {stories.map((s) => <StoryCard key={s.story_id} s={s} onOpen={() => navigate(`/stories/${s.story_id}`)} />)}
              </div>
            )}
          </section>
        )}

        {/* Tributes */}
        <section className="mt-12">
          <h2 className="font-display text-2xl font-semibold">Your tributes</h2>
          {loading ? (
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => <div key={i} className="h-40 rounded-2xl bg-[hsl(var(--muted))] animate-pulse" />)}
            </div>
          ) : events.length === 0 ? (
            <div className="mt-5 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-10 text-center">
              <h3 className="font-display text-xl font-semibold">No tributes yet</h3>
              <p className="mt-2 text-[hsl(var(--muted-foreground))] max-w-md mx-auto">Choose an occasion, add a photo and a few words, then share one link.</p>
              <button data-testid="empty-create-event-button" onClick={() => navigate("/create")}
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 transition-opacity">
                <Plus size={18} /> Create a tribute
              </button>
            </div>
          ) : (
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {events.map((ev) => {
                const t = getTheme(ev.type);
                return (
                  <button key={ev.event_id} data-testid={`event-card-${ev.event_id}`} onClick={() => navigate(`/events/${ev.event_id}`)}
                    className="text-left rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden hover:shadow-[0_12px_30px_rgba(20,16,12,0.10)] transition-shadow">
                    <div className="h-28 relative" style={{ background: `hsl(${t.accentSoft})` }}>
                      {ev.photos?.[0] ? <img src={fileUrl(ev.photos[0])} alt="" className="h-full w-full object-cover" /> :
                        <div className="h-full w-full grid place-items-center"><t.icon size={28} style={{ color: `hsl(${t.accent})` }} /></div>}
                      <div className="absolute top-3 right-3"><StatusChip status={ev.status} /></div>
                      {ev.my_role === "collaborator" && (
                        <div data-testid={`event-shared-chip-${ev.event_id}`} className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/85 text-[hsl(var(--foreground))] backdrop-blur">
                          Shared by {ev.shared_by || "team"}
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: `hsl(${t.accent})` }}>{t.short}</div>
                      <h3 className="mt-1 font-display text-xl font-semibold truncate">{ev.honoree_name}</h3>
                      <div className="mt-3 flex items-center gap-4 text-sm text-[hsl(var(--muted-foreground))]">
                        <span className="inline-flex items-center gap-1.5"><Users size={15} /> {ev.submission_counts?.total || 0}</span>
                        <span className="inline-flex items-center gap-1.5"><Clock size={15} /> {deadlineLabel(ev.settings?.deadline)}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
